import { useState, useEffect } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

/**
 * Detect current environment based on domain
 * - preview.emergentagent.com or localhost = "dev"
 * - selllocalhomes.co.uk = "prod"
 */
const detectEnvironment = () => {
  const hostname = window.location.hostname;
  
  if (hostname.includes('selllocalhomes.co.uk')) {
    console.log('[Navigation] Environment detected: PRODUCTION (selllocalhomes.co.uk)');
    return 'prod';
  }
  
  // Default to dev for preview and localhost
  console.log('[Navigation] Environment detected: DEVELOPMENT', hostname);
  return 'dev';
};

/**
 * Custom hook to fetch and organize navigation items
 */
export function useNavigation() {
  const [navigation, setNavigation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const environment = detectEnvironment();

  useEffect(() => {
    async function fetchNavigation() {
      try {
        setLoading(true);
        console.log('[Navigation] Fetching navigation for environment:', environment);
        const response = await axios.get(`${API}/navigation`, {
          params: { environment }
        });
        
        console.log('[Navigation] Received items from API:', response.data?.length || 0);
        
        // Build nested structure
        const items = response.data || [];
        const itemsMap = new Map();
        const rootItems = [];
        
        // First pass: create map of all items
        items.forEach(item => {
          itemsMap.set(item.nav_id, { ...item, children: [] });
        });
        
        // Second pass: build tree structure
        items.forEach(item => {
          if (item.parent_id && itemsMap.has(item.parent_id)) {
            // This is a child item
            itemsMap.get(item.parent_id).children.push(itemsMap.get(item.nav_id));
          } else if (!item.parent_id) {
            // This is a root item
            rootItems.push(itemsMap.get(item.nav_id));
          }
        });
        
        // Sort children by order
        rootItems.forEach(item => {
          if (item.children && item.children.length > 0) {
            item.children.sort((a, b) => a.order - b.order);
          }
        });
        
        // Sort root items by order
        rootItems.sort((a, b) => a.order - b.order);
        
        // Filter out disabled items (both root and children)
        const filterEnabled = (items) => {
          return items
            .filter(item => item.enabled)
            .map(item => ({
              ...item,
              children: item.children ? filterEnabled(item.children) : []
            }));
        };
        
        const enabledItems = filterEnabled(rootItems);
        
        console.log('[Navigation] Final enabled items:', enabledItems.length);
        enabledItems.forEach(item => {
          console.log(`  - ${item.title} (${item.environment}) ${item.children?.length ? `[${item.children.length} children]` : ''}`);
        });
        
        setNavigation(enabledItems);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch navigation:', err);
        setError(err);
        // Fallback to default navigation
        setNavigation(getDefaultNavigation());
      } finally {
        setLoading(false);
      }
    }

    fetchNavigation();
  }, [environment]);

  return { navigation, loading, error, environment };
}

/**
 * Fallback default navigation if API fails
 */
function getDefaultNavigation() {
  return [
    { nav_id: 'home', title: 'Home', path: '/', order: 1, enabled: true, children: [] },
    { nav_id: 'properties', title: 'Properties', path: '/properties', order: 2, enabled: true, children: [] },
    { nav_id: 'how-it-works', title: 'How It Works', path: '/how-it-works', order: 3, enabled: true, children: [] },
    { nav_id: 'prices', title: 'Prices', path: '/prices', order: 4, enabled: true, children: [] },
    { nav_id: 'contact', title: 'Contact', path: '/contact', order: 5, enabled: true, children: [] },
  ];
}

export default useNavigation;
